import React, { useState, useEffect } from 'react';
import './HtmlCssModal.css';

const HtmlCssModal = ({ isOpen, onClose, onApply, initialCode = '' }) => {
  const [code, setCode] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [originalFormattedCode, setOriginalFormattedCode] = useState('');

  // 리사이즈 핸들을 숨기는 함수
  const hideResizeHandles = () => {
    // 모든 가능한 리사이즈 핸들 선택자들
    const selectors = [
      '.react-resizable-handle',
      '[class*="resizable-handle"]',
      '[class*="Resizer"]', 
      '[class*="resizer"]',
      '[data-yoopta-block-type="Image"] [style*="cursor"]',
      '[data-yoopta-editor] [style*="cursor"][style*="resize"]',
      '[style*="position: absolute"][style*="cursor"][style*="resize"]',
      '[style*="position: absolute"][style*="width"][style*="height"][style*="cursor"]'
    ];
    
    selectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.pointerEvents = 'none';
          element.style.opacity = '0';
          element.style.zIndex = '-1';
        });
      } catch (e) {
        console.warn('Failed to hide elements with selector:', selector);
      }
    });

    // 인라인 스타일로 cursor가 resize 관련인 모든 요소 숨김
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      const style = window.getComputedStyle(element);
      const cursor = style.cursor;
      if (cursor && (
        cursor.includes('resize') || 
        cursor.includes('col-resize') ||
        cursor.includes('row-resize') ||
        cursor.includes('nw-resize') ||
        cursor.includes('ne-resize') ||
        cursor.includes('sw-resize') ||
        cursor.includes('se-resize')
      )) {
        // yoopta 관련 요소인지 확인
        const isYooptaRelated = element.closest('[data-yoopta-editor]') || 
                               element.closest('[class*="yoo-image"]') ||
                               element.closest('[data-yoopta-block-type="Image"]');
        
        if (isYooptaRelated) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.pointerEvents = 'none';
          element.style.opacity = '0';
        }
      }
    });
  };

  // 리사이즈 핸들을 다시 보이게 하는 함수
  const showResizeHandles = () => {
    // 1. 클래스 기반 리사이즈 핸들 복원
    const resizeSelectors = [
      '.react-resizable-handle',
      '[class*="resizable-handle"]',
      '[class*="Resizer"]', 
      '[class*="resizer"]'
    ];
    
    resizeSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (element.style.display === 'none') {
            element.style.display = '';
            element.style.visibility = '';
            element.style.pointerEvents = '';
            element.style.opacity = '';
            element.style.zIndex = '';
          }
        });
      } catch (e) {
        console.warn('Failed to restore elements with selector:', selector);
      }
    });

    // 2. yoopta 관련 숨겨진 요소들 복원
    const yooptaElements = document.querySelectorAll('[data-yoopta-editor] *, [class*="yoo-image"] *, [data-yoopta-block-type="Image"] *');
    yooptaElements.forEach(element => {
      if (element.style.display === 'none') {
        const computedStyle = window.getComputedStyle(element);
        // cursor가 resize 관련이거나 position이 absolute인 것들 복원
        if (computedStyle.cursor.includes('resize') || 
            computedStyle.position === 'absolute') {
          element.style.display = '';
          element.style.visibility = '';
          element.style.pointerEvents = '';
          element.style.opacity = '';
          element.style.zIndex = '';
        }
      }
    });

    // 3. 일반적인 숨겨진 요소들 중 리사이즈 관련 복원
    const hiddenElements = document.querySelectorAll('[style*="display: none"]');
    hiddenElements.forEach(element => {
      if (element.style.display === 'none') {
        // 클래스명 기반 체크
        const hasResizeClass = element.className.includes('resiz') || 
                              element.className.includes('Resiz');
        
        // 스타일 기반 체크 (computed style 포함)
        const computedStyle = window.getComputedStyle(element);
        const hasResizeCursor = element.style.cursor.includes('resize') ||
                               computedStyle.cursor.includes('resize');
        
        if (hasResizeClass || hasResizeCursor) {
          element.style.display = '';
          element.style.visibility = '';
          element.style.pointerEvents = '';
          element.style.opacity = '';
          element.style.zIndex = '';
        }
      }
    });
  };

  // 모달이 열릴 때 초기 코드 설정 및 리사이즈 핸들 숨김
  useEffect(() => {
    if (isOpen) {
      // body에 클래스 추가
      document.body.classList.add('html-modal-open');
      
      // 리사이즈 핸들 숨김 (약간의 지연을 두어 DOM이 준비된 후 실행)
      setTimeout(hideResizeHandles, 100);
      
      // MutationObserver로 동적으로 추가되는 리사이즈 핸들도 감지
      const observer = new MutationObserver(() => {
        hideResizeHandles();
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      
      // cleanup 함수를 위해 observer를 참조로 저장
      window.htmlModalObserver = observer;
      
      if (initialCode) {
        // HTML을 보기 좋게 포맷팅
        const formattedCode = formatHtml(initialCode);
        setCode(formattedCode);
        setOriginalFormattedCode(formattedCode);
        setIsEdited(false);
      } else {
        // 초기 코드가 없으면 빈 상태로 시작
        setCode('');
        setOriginalFormattedCode('');
        setIsEdited(false);
      }
    } else {
      // 모달이 닫힐 때 정리
      document.body.classList.remove('html-modal-open');
      showResizeHandles();
      
      if (window.htmlModalObserver) {
        window.htmlModalObserver.disconnect();
        delete window.htmlModalObserver;
      }
    }
    
    // cleanup: 컴포넌트 언마운트 시 정리
    return () => {
      document.body.classList.remove('html-modal-open');
      showResizeHandles();
      if (window.htmlModalObserver) {
        window.htmlModalObserver.disconnect();
        delete window.htmlModalObserver;
      }
    };
  }, [isOpen, initialCode]);

  // HTML 포맷팅 함수
  const formatHtml = (html) => {
    if (!html) return '';
    
    // style 태그 내용을 임시로 보관
    const styleBlocks = [];
    let tempHtml = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, content) => {
      styleBlocks.push({ match, content });
      return `__STYLE_BLOCK_${styleBlocks.length - 1}__`;
    });
    
    // 텍스트 노드를 보호
    const textNodes = [];
    tempHtml = tempHtml.replace(/>([^<]+)</g, (match, text) => {
      if (text.trim()) {
        textNodes.push(text);
        return `>__TEXT_NODE_${textNodes.length - 1}__<`;
      }
      return match;
    });
    
    // 기본적인 HTML 포맷팅
    let formatted = tempHtml
      .replace(/></g, '>\n<')  // 태그 사이에 줄바꿈 추가
      .replace(/\s*(<br\s*\/?>)\s*/gi, '\n$1\n')  // br 태그 전후 줄바꿈
      .replace(/\n\s*\n+/g, '\n')  // 연속된 빈 줄 제거
      .trim();
    
    // 들여쓰기 추가
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentSize = 2;
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
    
    formatted = lines.map(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      
      // 텍스트 노드 라인인지 확인
      const isTextNode = trimmedLine.includes('__TEXT_NODE_');
      
      // 닫는 태그면 들여쓰기 레벨 감소
      if (trimmedLine.startsWith('</')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      const indentedLine = ' '.repeat(indentLevel * indentSize) + trimmedLine;
      
      // 태그 이름 추출
      const tagMatch = trimmedLine.match(/^<\/?(\w+)/);
      const tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
      
      // 여는 태그면 들여쓰기 레벨 증가 (자체 닫힘 태그 제외)
      if (trimmedLine.startsWith('<') && !trimmedLine.startsWith('</')) {
        const isSelfClosing = selfClosingTags.includes(tagName) || trimmedLine.endsWith('/>');
        
        // 같은 줄에 닫는 태그가 있는지 확인
        const hasClosingTag = trimmedLine.includes(`</${tagName}>`);
        
        if (!isSelfClosing && !hasClosingTag && !isTextNode) {
          indentLevel++;
        }
      }
      
      return indentedLine;
    }).filter(line => line !== '').join('\n');
    
    // 텍스트 노드 복원
    textNodes.forEach((text, index) => {
      formatted = formatted.replace(`__TEXT_NODE_${index}__`, text);
    });
    
    // style 태그 복원 및 포맷팅
    styleBlocks.forEach((block, index) => {
      const formattedStyle = formatCss(block.content);
      const styleTag = `<style>\n${formattedStyle}\n</style>`;
      formatted = formatted.replace(`__STYLE_BLOCK_${index}__`, styleTag);
    });
    
    // 빈 줄 정리
    formatted = formatted.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    return formatted;
  };
  
  // CSS 포맷팅 함수
  const formatCss = (css) => {
    if (!css.trim()) return '';
    
    return css
      .replace(/\s*{\s*/g, ' {\n  ')  // 여는 중괄호 후 줄바꿈과 들여쓰기
      .replace(/;\s*/g, ';\n  ')  // 세미콜론 후 줄바꿈과 들여쓰기
      .replace(/\s*}\s*/g, '\n}\n')  // 닫는 중괄호 전후 줄바꿈
      .replace(/:\s*/g, ': ')  // 콜론 후 공백
      .replace(/,\s*/g, ', ')  // 쉼표 후 공백
      .replace(/\n\s*\n+/g, '\n')  // 연속된 빈 줄 제거
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (trimmed.endsWith('{')) return '  ' + trimmed;
        if (trimmed === '}') return '  ' + trimmed;
        if (trimmed) return '    ' + trimmed;
        return '';
      })
      .filter(line => line)
      .join('\n');
  };

  const handleApply = () => {
    // 코드가 변경되지 않았으면 그냥 닫기
    if (!isEdited || code === originalFormattedCode) {
      handleCancel();
      return;
    }
    
    // body 클래스 제거 및 리사이즈 핸들 복원
    document.body.classList.remove('html-modal-open');
    
    // 리사이즈 핸들 복원
    showResizeHandles();
    
    // Observer 정리
    if (window.htmlModalObserver) {
      window.htmlModalObserver.disconnect();
      delete window.htmlModalObserver;
    }
    
    onApply(code);
    onClose();
    // 모달 닫힐 때 코드 초기화
    setCode('');
    setOriginalFormattedCode('');
    setPreviewMode(false);
    setIsEdited(false);
  };

  const handleCancel = () => {
    // body 클래스 제거 및 리사이즈 핸들 복원
    document.body.classList.remove('html-modal-open');
    
    // 리사이즈 핸들 복원
    showResizeHandles();
    
    // Observer 정리
    if (window.htmlModalObserver) {
      window.htmlModalObserver.disconnect();
      delete window.htmlModalObserver;
    }
    
    onClose();
    setCode('');
    setOriginalFormattedCode('');
    setPreviewMode(false);
    setIsEdited(false);
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    setIsEdited(true);
  };

  // 미리보기를 위한 코드 처리
  const getPreviewCode = () => {
    // style 태그가 포함된 코드를 그대로 반환하여 CSS가 적용되도록 함
    return code || '';
  };

  if (!isOpen) return null;

  return (
    <div className="html-css-modal-overlay" onClick={handleCancel}>
      <div className="html-css-modal" onClick={(e) => e.stopPropagation()}>
        <div className="html-css-modal-header">
          <h2>
            HTML/CSS 코드 편집기
            {initialCode && !isEdited && (
              <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                (현재 에디터 내용)
              </span>
            )}
            {isEdited && (
              <span style={{ fontSize: '14px', color: '#f97316', marginLeft: '10px' }}>
                (수정됨)
              </span>
            )}
          </h2>
          <button className="html-css-modal-close" onClick={handleCancel}>×</button>
        </div>
        
        <div className="html-css-modal-tabs">
          <button 
            className={!previewMode ? 'active' : ''} 
            onClick={() => setPreviewMode(false)}
          >
            코드 편집
          </button>
          <button 
            className={previewMode ? 'active' : ''} 
            onClick={() => setPreviewMode(true)}
          >
            미리보기
          </button>
        </div>

        <div className="html-css-modal-content">
          {!previewMode ? (
            <div className="html-css-editors">
              <div className="code-editor-section">
                <label>HTML/CSS 코드</label>
                <textarea
                  value={code}
                  onChange={handleCodeChange}
                  placeholder={`HTML과 CSS 코드를 함께 입력하세요...

예시:
<style>
  .my-card {
    background: #f0f0f0;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }
  .my-title {
    color: #333;
    font-size: 24px;
    margin-bottom: 10px;
  }
</style>

<div class="my-card">
  <h2 class="my-title">안녕하세요</h2>
  <p>이것은 예시 내용입니다.</p>
</div>`}
                  rows={20}
                  style={{ fontFamily: 'Monaco, Menlo, Consolas, monospace' }}
                />
              </div>
            </div>
          ) : (
            <div className="html-css-preview">
              <div className="preview-label">미리보기</div>
              <div className="preview-iframe-container">
                <iframe
                  className="preview-iframe"
                  srcDoc={getPreviewCode()}
                  title="HTML 미리보기"
                  sandbox="allow-scripts allow-same-origin"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="html-css-modal-footer">
          <button className="cancel-button" onClick={handleCancel}>
            취소
          </button>
          <button 
            className="apply-button" 
            onClick={handleApply}
            disabled={!code.trim()}
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default HtmlCssModal;